import { FormProvider, useFieldArray, UseFormReturn } from 'react-hook-form';
import { Plus } from 'lucide-react';
import { IPunishment } from '@/states/punishment.state';

export const isRichTextEmpty = (html: string | null | undefined): boolean => {
  if (!html) return true;
  const text = html
    .replace(/<[^>]*>/g, '')
    .replace(/&nbsp;/g, ' ')
    .trim();
  return text === '';
};

interface PunishmentFormProps {
  formControl: UseFormReturn<IPunishment> & Record<string, any>;
  formRef: React.RefObject<HTMLFormElement>;
  handleSave: () => void;
  disabled?: boolean;
}

export const PunishmentForm = ({ formControl, formRef, handleSave, disabled }: PunishmentFormProps) => {
  const {
    control,
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = formControl;

  const severityLevel = watch('severity_level');
  const definitionWarning = watch('definition_warning');
  const sanctionDescription = watch('sanction_description');

  const {
    fields: sanctionFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'sanctions',
  });

  return (
    <FormProvider {...formControl}>
      <form ref={formRef} onSubmit={handleSubmit(handleSave)} className="mt-4 px-5">
        {/* Jenis Peringatan */}
        <div className="mb-5">
          <label htmlFor="severity_level" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Jenis Peringatan <span className="text-red-500">*</span>
          </label>

          <input
            value={severityLevel || '-'}
            disabled
            className="w-full h-10 px-3 border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400"
          />
        </div>

        {/* Definisi Peringatan */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Definisi Peringatan <span className="text-red-500">*</span>
          </label>

          <div
            className="w-full min-h-[40px] px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{
              __html: definitionWarning || '-',
            }}
          />
        </div>

        {/* Masa Berlakunya Sanksi */}
        <div className="mb-5">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
            Masa Berlakunya Sanksi <span className="text-red-500">*</span>
          </label>

          <div
            className="w-full min-h-[40px] px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-gray-700 dark:text-gray-300 prose prose-sm dark:prose-invert max-w-none"
            dangerouslySetInnerHTML={{
              __html: sanctionDescription || '-',
            }}
          />
        </div>

        {/* Bentuk/Tindakan Pelanggaran & Sanksi/Denda - Table Layout */}
        <div className="mb-5">
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
              Bentuk/Tindakan Pelanggaran &amp; Sanksi/Denda
            </label>
            {!disabled && (
              <button
                type="button"
                onClick={() => append({ warning_clause: '', fee: '' })}
                className="flex items-center gap-1.5 text-sm border border-blue-500 dark:border-blue-600 rounded-lg px-3 py-1.5 text-blue-600 dark:text-blue-400 hover:bg-blue-50 dark:hover:bg-blue-900/30 transition-colors"
              >
                <Plus size={14} />
                <span className="text-sm">Tambah</span>
              </button>
            )}
          </div>

          {/* Table Structure */}
          <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden bg-white dark:bg-gray-800">
            {/* Table Header */}
            <div className="grid grid-cols-2 gap-0 bg-gray-50 dark:bg-gray-900 border-b border-gray-200 dark:border-gray-700">
              <div className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                Bentuk/Tindakan Pelanggaran <span className="text-red-500">*</span>
              </div>
              <div className="px-4 py-3 text-xs font-semibold text-gray-600 dark:text-gray-300 uppercase tracking-wide">
                Sanksi/Denda <span className="text-red-500">*</span>
              </div>
            </div>

            {/* Table Body */}
            <div className="divide-y divide-gray-100 dark:divide-gray-700">
              {sanctionFields.length === 0 ? (
                <div className="px-4 py-10 text-center text-sm text-gray-400 dark:text-gray-500">
                  Belum ada pasal peringatan. Klik tombol "Tambah" untuk menambah.
                </div>
              ) : (
                sanctionFields.map((sanction, index) => {
                  const sanctionErrs = (
                    errors.sanctions as
                      | Array<{
                          warning_clause?: { message?: string };
                          fee?: { message?: string };
                        }>
                      | undefined
                  )?.[index];

                  return (
                    <div key={sanction.id}>
                      <div className="grid grid-cols-2 gap-0 bg-white dark:bg-gray-800 hover:bg-gray-50/40 dark:hover:bg-gray-700/40">
                        {/* Warning Clause Column */}
                        <div className="px-4 py-4 border-r border-gray-100 dark:border-gray-700 flex items-start min-h-[50px]">
                          <input
                            {...register(`sanctions.${index}.warning_clause`, {
                              required: 'Pasal peringatan wajib diisi',
                            })}
                            disabled={disabled}
                            placeholder="nama tindakan pelanggaran"
                            className={`w-full h-9 px-3 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500 ${
                              sanctionErrs?.warning_clause
                                ? 'border-red-400 dark:border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`}
                          />
                        </div>

                        {/* Fee Column */}
                        <div className="px-4 py-4 flex items-start gap-2 min-h-[50px]">
                          <input
                            {...register(`sanctions.${index}.fee`, {
                              required: 'Sanksi/denda wajib diisi',
                              maxLength: { value: 255, message: 'Maksimal 255 karakter' },
                            })}
                            type="text"
                            disabled={disabled}
                            maxLength={255}
                            placeholder="Masukkan sanksi/denda"
                            className={`flex-1 h-9 px-3 text-sm border rounded-lg focus:outline-none focus:ring-1 focus:ring-blue-500 disabled:bg-gray-50 dark:disabled:bg-gray-600 disabled:text-gray-400 dark:disabled:text-gray-500 ${
                              sanctionErrs?.fee
                                ? 'border-red-400 dark:border-red-500'
                                : 'border-gray-300 dark:border-gray-600'
                            } bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500`}
                          />
                          {!disabled && sanctionFields.length > 1 && (
                            <button
                              type="button"
                              onClick={() => remove(index)}
                              className="px-3 py-1 text-sm border border-red-300 dark:border-red-600 rounded-md text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors flex-shrink-0 h-9"
                            >
                              Hapus
                            </button>
                          )}
                        </div>
                      </div>

                      {/* Error Messages Row */}
                      {(sanctionErrs?.warning_clause || sanctionErrs?.fee) && (
                        <div className="grid grid-cols-2 gap-0 bg-red-50/30 dark:bg-red-900/10 border-t border-red-100 dark:border-red-900/20">
                          <div className="px-4 py-2 border-r border-gray-100 dark:border-gray-700 text-xs text-red-600 dark:text-red-400">
                            {sanctionErrs?.warning_clause?.message}
                          </div>
                          <div className="px-4 py-2 text-xs text-red-600 dark:text-red-400">
                            {sanctionErrs?.fee?.message}
                          </div>
                        </div>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      </form>
    </FormProvider>
  );
};
